<template>
	<div v-if="request" class="modal-mask chat-rate" @click.self="skip" @keydown.esc.prevent="skip">
		<div
			ref="dialog"
			class="modal-container chat-rate__dialog"
			role="dialog"
			aria-modal="true"
			:aria-label="$t('chat.rate_title')"
			tabindex="-1"
		>
			<div class="flex flex-row items-center border-b-default border-border-color">
				<h2 class="flex flex-1 items-center p-4 font-semibold">{{ $t('chat.rate_title') }}</h2>
				<button
					type="button"
					class="chat-rate__close mr-4"
					:aria-label="$t('chat.close')"
					:disabled="submitting"
					@click="skip"
				>
					<app-icon class="text-2xl text-secondary" icon="cancel" />
				</button>
			</div>
			<div class="chat-rate__body">
				<p class="chat-rate__description">{{ request.message || $t('chat.rate_description') }}</p>
				<div class="chat-rate__track" role="group" :aria-label="$t('chat.rate_title')">
					<button
						v-for="value in values"
						:key="value"
						type="button"
						class="chat-rate__value"
						:class="{ 'chat-rate__value--on': value === rating }"
						:aria-pressed="value === rating ? 'true' : 'false'"
						@click="rating = value"
					>
						{{ value }}
					</button>
				</div>
				<div class="chat-rate__anchors">
					<span>{{ $t('chat.rate_low') }}</span>
					<span>{{ $t('chat.rate_high') }}</span>
				</div>
				<p class="chat-rate__feedback" aria-live="polite">{{ $t(feedbackKey) }}</p>
				<button type="button" class="chat-rate__submit" :disabled="submitting" @click="submit">
					<app-icon v-if="submitting" icon="spin-loading" />
					{{ $t('chat.rate_submit') }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
	import AppIcon from '@/components/app/AppIcon';
	import { channelNameOf } from '@/services/chat/channels';
	import { rateChat } from '@/services/chat/chatApi';
	import { isChatRated } from '@/services/chat/chatFirestore';
	import { chatStore, closeRateRequest, showRateRequest, takeRateRequest } from '@/services/chat/chatStore';
	import UtilityService from '@/services/UtilityService';
	import { showChatToast } from './chatToasts';

	const RATING_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
	const DEFAULT_RATING = 10;

	const feedbackKeyOf = (value) => {
		if (value <= 2) {
			return 'chat.rate_feedback_bad';
		}
		if (value <= 4) {
			return 'chat.rate_feedback_poor';
		}
		if (value <= 6) {
			return 'chat.rate_feedback_average';
		}
		if (value <= 8) {
			return 'chat.rate_feedback_good';
		}
		return 'chat.rate_feedback_excellent';
	};

	export default {
		name: 'ChatRateDialog',
		components: { AppIcon },
		data: () => ({
			rating: DEFAULT_RATING,
			submitting: false,
			checking: false,
			values: RATING_VALUES,
		}),
		computed: {
			request() {
				return chatStore.rateRequest;
			},
			waiting() {
				return chatStore.rateQueue.length;
			},
			feedbackKey() {
				return feedbackKeyOf(this.rating);
			},
		},
		watch: {
			request(request) {
				if (request) {
					this.rating = DEFAULT_RATING;
					this.$nextTick(() => {
						if (this.$refs.dialog) {
							this.$refs.dialog.focus();
						}
					});
				} else {
					this.next();
				}
			},
			waiting() {
				this.next();
			},
		},
		methods: {
			async next() {
				if (this.checking) {
					return;
				}
				const request = takeRateRequest();
				if (!request) {
					return;
				}
				const queue = chatStore.rateQueue;
				this.checking = true;
				const rated = await isChatRated(request).catch(() => false);
				this.checking = false;
				if (!this.alive || queue !== chatStore.rateQueue) {
					return;
				}
				if (rated) {
					this.next();
				} else {
					showRateRequest(request);
				}
			},
			skip() {
				if (!this.submitting) {
					closeRateRequest();
				}
			},
			async submit() {
				const { request } = this;
				if (!request || this.submitting) {
					return;
				}
				this.submitting = true;
				try {
					await rateChat({
						channelName: channelNameOf(request.typeId),
						chatId: request.chatId,
						value: this.rating,
					});
					if (chatStore.rateRequest === request) {
						closeRateRequest();
					}
				} catch (error) {
					showChatToast({ kind: 'error', text: UtilityService.extractErrorMessage(error) });
				} finally {
					if (this.alive) {
						this.submitting = false;
					}
				}
			},
		},
		created() {
			this.alive = true;
			this.next();
		},
		beforeUnmount() {
			this.alive = false;
		},
	};
</script>

<style lang="scss" scoped>
	$chat-green: #08cc04;
	$chat-green-dark: #037800;
	$ink: #1a202c;
	$muted: #718096;

	.chat-rate {
		z-index: 10003;
	}

	.chat-rate__dialog {
		width: 440px;
		max-width: calc(100vw - 32px);
		outline: none;
	}

	.chat-rate__close {
		display: flex;
		align-items: center;
		background: transparent;
		border: 0;
		cursor: pointer;

		&:disabled {
			cursor: default;
			opacity: 0.5;
		}
	}

	.chat-rate__body {
		padding: 16px 20px 20px;
	}

	.chat-rate__description {
		margin-bottom: 18px;
		font-size: 14px;
		line-height: 1.4;
		text-align: center;
		color: $muted;
	}

	.chat-rate__track {
		display: flex;
		gap: 2px;
		padding: 4px;
		background: #edf2f7;
		border-radius: 12px;
	}

	.chat-rate__value {
		flex: 1;
		height: 36px;
		font-size: 14px;
		font-weight: 700;
		color: $ink;
		background: transparent;
		border: 0;
		border-radius: 9px;
		cursor: pointer;
		font-variant-numeric: tabular-nums;
		transition: background-color 150ms ease, color 150ms ease;

		&:hover {
			background: var(--white);
		}

		&:focus-visible {
			outline: 2px solid $chat-green;
			outline-offset: 1px;
		}
	}

	.chat-rate__value--on,
	.chat-rate__value--on:hover {
		color: var(--white);
		background: $chat-green;
	}

	.chat-rate__anchors {
		display: flex;
		justify-content: space-between;
		margin-top: 6px;
		padding: 0 2px;
		font-size: 12px;
		font-weight: 600;
		color: $muted;
	}

	.chat-rate__feedback {
		margin-top: 14px;
		font-size: 16px;
		font-weight: 800;
		text-align: center;
		color: $chat-green-dark;
	}

	.chat-rate__submit {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		width: 100%;
		margin-top: 18px;
		padding: 10px 16px;
		font-size: 14px;
		font-weight: 700;
		color: var(--white);
		background: var(--success);
		border: 1px solid var(--success);
		border-radius: 9999px;
		cursor: pointer;

		&:disabled {
			cursor: default;
			opacity: 0.7;
		}
	}
</style>
